import { useEffect, useState } from "react";
import { UploadIcon, XIcon } from "lucide-react";
import type { PayrollAdjustmentDraft } from "../../types";

type AdjustmentFormProps = {
  isOpen: boolean;
  onClose: () => void;
  employeeName: string;
  variant: "quick" | "structured";
  initialValue?: PayrollAdjustmentDraft | null;
  onSave?: (input: PayrollAdjustmentDraft) => void | Promise<void>;
};

const ADJUSTMENT_TYPES = [
  "Additional Pay",
  "Pay Reduction",
  "Correction",
  "Attendance Bonus",
  "Absence Penalty",
  "Other",
] as const;
const DEFAULT_ATTENDANCE_BONUS = 100;
const DEFAULT_ABSENCE_PENALTY = 50;
const CURRENT_PERIOD_LABEL = "Current payroll period";
const CHANGE_CATEGORIES = [
  "New Position",
  "Position Rate Change",
  "New Stipend",
  "Stipend Modification",
  "One-Time Payment",
  "Termination/Leave",
] as const;

const today = () => new Date().toISOString().split("T")[0] ?? "";

export function AdjustmentForm({
  isOpen,
  onClose,
  employeeName,
  variant,
  initialValue,
  onSave,
}: AdjustmentFormProps) {
  const [type, setType] =
    useState<(typeof ADJUSTMENT_TYPES)[number]>("Additional Pay");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] =
    useState<(typeof CHANGE_CATEGORIES)[number]>("New Position");
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setType(
      (initialValue?.type as (typeof ADJUSTMENT_TYPES)[number] | undefined) ??
        "Additional Pay",
    );
    setAmount(
      initialValue?.amount == null ? "" : String(initialValue.amount),
    );
    setDescription(initialValue?.description ?? "");
    setEffectiveDate(today());
    setNotes("");
  }, [initialValue, isOpen]);

  useEffect(() => {
    if (type === "Attendance Bonus") {
      setAmount(DEFAULT_ATTENDANCE_BONUS.toString());
      setDescription(`Perfect attendance bonus - ${CURRENT_PERIOD_LABEL}`);
    } else if (type === "Absence Penalty") {
      setAmount((-DEFAULT_ABSENCE_PENALTY).toString());
      setDescription(`Absence penalty - ${CURRENT_PERIOD_LABEL}`);
    }
  }, [type]);

  if (!isOpen) {
    return null;
  }

  const isQuickValid =
    amount !== "" && description.length >= 10 && description.length <= 250;
  const isStructuredValid = amount !== "" && notes.length >= 10;

  const handleSave = async () => {
    if ((variant === "quick" && !isQuickValid) || (variant === "structured" && !isStructuredValid)) {
      return;
    }

    const adjustmentType =
      variant === "structured"
        ? "Correction"
        : type === "Attendance Bonus"
          ? "Additional Pay"
          : type === "Absence Penalty"
            ? "Pay Reduction"
            : type;
    const payloadDescription =
      variant === "structured"
        ? `${category}: ${notes}`
        : description;

    await onSave?.({
      ...initialValue,
      type: adjustmentType,
      amount: Number(amount),
      description: payloadDescription,
      timestamp: new Date(`${effectiveDate}T12:00:00Z`).toISOString(),
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative flex max-h-[90vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[12px] bg-surface-light shadow-xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 p-5">
          <div>
            <h2 className="font-heading text-lg font-semibold text-iron-grey">
              {variant === "quick" ? "Quick Add Adjustment" : "Submit Change Request"}
            </h2>
            <p className="mt-0.5 font-body text-sm text-text-secondary">
              For {employeeName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary transition-colors hover:text-iron-grey"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto p-6">
          {variant === "quick" ? (
            <>
              <div>
                <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                  Adjustment Type
                </label>
                <select
                  value={type}
                  onChange={(event) =>
                    setType(
                      event.target.value as (typeof ADJUSTMENT_TYPES)[number],
                    )
                  }
                  className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {ADJUSTMENT_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                  Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-secondary">
                    $
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-[6px] border border-border bg-surface-light py-2 pl-7 pr-3 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <p className="mt-1 font-body text-xs text-text-secondary">
                  Use negative values for reductions
                </p>
              </div>

              <div>
                <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                  Description <span className="text-strawberry">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={3}
                  maxLength={250}
                  placeholder="Brief explanation of this adjustment..."
                  className="w-full resize-none rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="mt-1 font-body text-xs text-text-secondary">
                  {description.length}/250 characters (minimum 10)
                </p>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                  Change Category
                </label>
                <select
                  value={category}
                  onChange={(event) =>
                    setCategory(
                      event.target.value as (typeof CHANGE_CATEGORIES)[number],
                    )
                  }
                  className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {CHANGE_CATEGORIES.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                  Effective Date
                </label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(event) => setEffectiveDate(event.target.value)}
                  className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div>
                <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                  Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-secondary">
                    $
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-[6px] border border-border bg-surface-light py-2 pl-7 pr-3 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                  Supporting Notes <span className="text-strawberry">*</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={4}
                  placeholder="Detailed explanation of this change request..."
                  className="w-full resize-none rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="mt-1 font-body text-xs text-text-secondary">
                  Minimum 10 characters
                </p>
              </div>

              <div>
                <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                  Attachments (Optional)
                </label>
                <div className="cursor-pointer rounded-[8px] border-2 border-dashed border-border p-6 text-center transition-colors hover:border-primary">
                  <UploadIcon className="mx-auto mb-2 h-8 w-8 text-text-tertiary" />
                  <p className="font-body text-sm text-iron-grey">
                    Click to upload or drag and drop
                  </p>
                  <p className="font-body text-xs text-text-secondary">
                    PDF or images, max 5MB
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-background-light p-5">
          <button
            onClick={onClose}
            className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            disabled={variant === "quick" ? !isQuickValid : !isStructuredValid}
            onClick={() => void handleSave()}
            className="rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {variant === "quick" ? "Save Adjustment" : "Submit Change Request"}
          </button>
        </div>
      </div>
    </div>
  );
}
